import React, { useState, useEffect } from 'react';
import { X, Package, ArrowRight, Search } from 'lucide-react';
import UnitTypeTierModal from './UnitTypeTierModal';

interface Client {
    id: number;
    company_name: string;
    marque?: string | null;
    email: string;
}

interface Consumable {
    id: number;
    title: string;
    marque?: string | null;
    base_price: number;
    currency: string;
    unit: string;
    unit_id: number;
    batch_size?: number;
    volume_ml?: number;
}

interface BuyerPricingItem {
    id: number;
    consumable_id: number;
    custom_price: number;
    discount_percentage: number | null;
    free_units?: number | null;
    tiers?: Array<{
        id: number;
        tier_number: number;
        min_quantity: number;
        max_quantity?: number | null;
        discount_percentage?: number | null;
        free_units?: number | null;
    }>;
}

interface ClientPricing {
    id: number;
    reference_number: string;
    name: string;
    client_id: number;
    description: string | null;
    status: 'draft' | 'validated' | 'archived';
    payment_term: string;
    pricing_items: BuyerPricingItem[];
    Client?: Client;
}

interface UnitType {
    unit_id: number;
    unit_name: string;
    volume: number;
    product_count: number;
    consumable_ids: number[];
}

interface BpuModificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    editing: ClientPricing | null;
    consumables: Consumable[];
    onSave: (data: any) => Promise<void>;
}

const BpuModificationModal: React.FC<BpuModificationModalProps> = ({
    isOpen,
    onClose,
    editing,
    consumables,
    onSave
}) => {
    // State
    const [selectedConsumableIds, setSelectedConsumableIds] = useState<Set<number>>(new Set());
    const [consumablesSearch, setConsumablesSearch] = useState<string>('');
    const [globalPricingMode, setGlobalPricingMode] = useState<'percent' | 'free_units'>('percent');
    const [globalApplyValue, setGlobalApplyValue] = useState<string>('');
    const [productPricing, setProductPricing] = useState<Record<number, any>>({});

    // Unit-based pricing (gamme-level tiers)
    const [showUnitTypeTierModal, setShowUnitTypeTierModal] = useState(false);
    const [selectedUnitType, setSelectedUnitType] = useState<UnitType | null>(null);
    const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);

    // Fetch actual unit types from BPU products
    useEffect(() => {
        if (isOpen && editing) {
            const unitTypeMap = new Map<string, UnitType>();

            editing.pricing_items.forEach(item => {
                const consumable = consumables.find(c => c.id === item.consumable_id);
                if (consumable && consumable.unit) {
                    const unitName = consumable.unit.trim();
                    const volume = consumable.batch_size ?? 1;
                    const key = `${consumable.unit_id}:${volume}`;

                    if (!unitTypeMap.has(key)) {
                        unitTypeMap.set(key, {
                            unit_id: consumable.unit_id,
                            unit_name: unitName,
                            volume,
                            consumable_ids: [],
                            product_count: 0
                        });
                    }

                    const unitType = unitTypeMap.get(key)!;
                    unitType.consumable_ids.push(consumable.id);
                    unitType.product_count++;
                }
            });

            setUnitTypes(Array.from(unitTypeMap.values()).sort((a, b) =>
                a.unit_name.localeCompare(b.unit_name) || a.volume - b.volume
            ));
        }
    }, [isOpen, editing, consumables]);

    // Initialize selected consumables and pricing when modal opens
    useEffect(() => {
        if (isOpen && editing) {
            const existingIds = new Set(editing.pricing_items.map(item => item.consumable_id));
            setSelectedConsumableIds(existingIds);

            const existingPricing: Record<number, any> = {};
            editing.pricing_items.forEach(item => {
                existingPricing[item.consumable_id] = {
                    custom_price: item.custom_price,
                    discount_percent: item.discount_percentage || 0,
                    free_units: item.free_units,
                    tiers: item.tiers || []
                };
            });
            setProductPricing(existingPricing);
        }
    }, [isOpen, editing]);

    if (!isOpen || !editing) return null;

    const activeClient = editing.Client;
    const activeClientBrandNormalized = (activeClient?.marque || '').trim().toUpperCase();

    const consumablesForModal = consumables.filter((c) => {
        if (!activeClientBrandNormalized) return true;
        return ((c.marque || '').trim().toUpperCase() === activeClientBrandNormalized);
    });

    const selectedConsumableIdsForModal = new Set(
        [...selectedConsumableIds].filter((id) => consumablesForModal.some(c => c.id === id))
    );

    // Handle unit type selection
    const handleUnitTypeClick = (unitType: UnitType): void => {
        setSelectedUnitType(unitType);
        setShowUnitTypeTierModal(true);
    };

    // Handle successful tier application - just close and refresh
    const handleUnitTypeTierSuccess = (): void => {
        // Tiers have been applied, modal will refresh automatically
        setShowUnitTypeTierModal(false);
    };

    // Toggle consumable selection
    const toggleConsumableSelection = (id: number): void => {
        const newSelected = new Set(selectedConsumableIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedConsumableIds(newSelected);
    };

    // Select/deselect all
    const selectAllConsumables = (): void => {
        setSelectedConsumableIds(new Set(consumablesForModal.map(c => c.id)));
    };

    const deselectAllConsumables = (): void => {
        setSelectedConsumableIds(new Set());
    };

    // Get product pricing
    const getProductPricing = (consumableId: number) => {
        return productPricing[consumableId] || {
            custom_price: 0,
            discount_percent: 0,
            free_units: undefined,
            tiers: []
        };
    };

    // Handle mode switch
    const handleModeSwitch = (newMode: 'percent' | 'free_units'): void => {
        if (newMode === globalPricingMode) return;
        setGlobalPricingMode(newMode);
    };

    // Apply global value
    const applyGlobalValue = (): void => {
        const rawValue: string = globalApplyValue.replace(',', '.').trim();
        const parsed: number = Number.parseFloat(rawValue);

        if (Number.isNaN(parsed)) return;

        const value: number = parsed;
        const clampedValue: number = globalPricingMode === 'percent'
            ? Math.min(100, Math.max(0, value))
            : Math.max(0, value);

        if (globalPricingMode === 'percent') {
            applyGlobalDiscount(clampedValue);
        } else {
            applyGlobalFreeUnits(clampedValue);
        }

        setGlobalApplyValue('');
    };

    // Apply global discount
    const applyGlobalDiscount = (discountPercent: number): void => {
        const newPricing: typeof productPricing = {};
        selectedConsumableIdsForModal.forEach(consumableId => {
            const consumable = consumables.find(c => c.id === consumableId);
            if (!consumable) return;

            const basePrice = Number(consumable.base_price ?? 0);
            const newCustomPrice = Number((basePrice * (1 - discountPercent / 100)).toFixed(2));
            const currentPricing = getProductPricing(consumableId);

            newPricing[consumableId] = {
                ...currentPricing,
                custom_price: newCustomPrice,
                discount_percent: discountPercent
            };
        });
        setProductPricing(prev => ({ ...prev, ...newPricing }));
    };

    // Apply global free units
    const applyGlobalFreeUnits = (freeUnits: number): void => {
        const newPricing: typeof productPricing = {};
        selectedConsumableIdsForModal.forEach(consumableId => {
            const currentPricing = getProductPricing(consumableId);
            newPricing[consumableId] = {
                ...currentPricing,
                free_units: freeUnits
            };
        });
        setProductPricing(prev => ({ ...prev, ...newPricing }));
    };

    // Handle save
    const handleSave = async () => {
        const pricingData = Array.from(selectedConsumableIds).map(consumableId => {
            const pricing = getProductPricing(consumableId);
            return {
                consumable_id: consumableId,
                custom_price: pricing.custom_price,
                discount_percentage: pricing.discount_percent,
                free_units: pricing.free_units,
                tiers: pricing.tiers
            };
        });

        await onSave(pricingData);
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 sm:p-6 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 shrink-0" />
                            <div className="min-w-0">
                                <h2 className="text-base sm:text-xl font-semibold text-slate-900 dark:text-white">
                                    Modifier BPU
                                </h2>
                                <p className="text-xs sm:text-sm text-slate-500 truncate">{editing.reference_number}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors shrink-0"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
                        {/* Client info */}
                        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 sm:p-4">
                            <div className="text-xs sm:text-sm">
                                <span className="font-medium text-slate-700 dark:text-slate-300">Client</span>
                                <div className="text-slate-900 dark:text-white font-semibold mt-0.5 sm:mt-1">
                                    {activeClient?.company_name || 'N/A'}
                                </div>
                            </div>
                        </div>

                        {/* Products header */}
                        <div className="flex items-center justify-between gap-2">
                            <h3 className="font-bold text-sm sm:text-lg flex items-center gap-1.5 sm:gap-2">
                                <Package className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 shrink-0" />
                                Produits ({selectedConsumableIdsForModal.size})
                            </h3>
                            <div className="flex gap-2 text-[11px] sm:text-xs shrink-0">
                                <button
                                    onClick={selectAllConsumables}
                                    className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 font-medium"
                                >
                                    Tout
                                </button>
                                <span className="text-slate-300">|</span>
                                <button
                                    onClick={deselectAllConsumables}
                                    className="text-slate-600 hover:text-slate-700 dark:text-slate-400 font-medium"
                                >
                                    Aucun
                                </button>
                            </div>
                        </div>

                        {/* Unit-based pricing section - Always visible */}
                        <div className="p-3 sm:p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                            <h4 className="text-xs sm:text-sm font-semibold text-orange-900 dark:text-orange-100 mb-2 sm:mb-3">
                                Appliquer par unité
                            </h4>
                            {unitTypes.length > 0 ? (
                                <div className="space-y-2">
                                    {unitTypes.map((unitType, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleUnitTypeClick(unitType)}
                                            className="w-full flex items-center justify-between p-3 bg-white dark:bg-slate-800 border border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-colors group"
                                        >
                                            <div className="text-left">
                                                <div className="font-medium text-slate-900 dark:text-white">
                                                    {unitType.unit_name}
                                                </div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                    {unitType.product_count} produit{unitType.product_count > 1 ? 's' : ''}
                                                </div>
                                            </div>
                                            <ArrowRight className="w-5 h-5 text-orange-600 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-600 dark:text-slate-400 italic">
                                    Aucun type d'unité détecté. Les produits doivent avoir un format de titre: "Nom | Détails | unité"
                                </p>
                            )}
                        </div>

                        {/* Mode selection and global apply */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Mode de réduction :</span>
                            <div className="flex bg-slate-200 dark:bg-slate-700 rounded-lg p-1">
                                <button
                                    onClick={() => handleModeSwitch('percent')}
                                    className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${globalPricingMode === 'percent'
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                        }`}
                                >
                                    % Pourcentage
                                </button>
                                {false && (
                                    <button
                                        onClick={() => handleModeSwitch('free_units')}
                                        className={`px-6 py-2 text-sm font-medium rounded-md transition-all ${globalPricingMode === 'free_units'
                                            ? 'bg-purple-600 text-white shadow-sm'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                            }`}
                                    >
                                        UG Unités Gratuites
                                    </button>
                                )}
                            </div>

                            {false && selectedConsumableIdsForModal.size > 0 && (
                                <div className="flex items-center gap-2 ml-auto">
                                    <span className="text-xs text-slate-500">Appliquer à tous sélectionnés :</span>
                                    <input
                                        type="text"
                                        placeholder={globalPricingMode === 'percent' ? '10' : '6'}
                                        className="w-20 px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-right"
                                        value={globalApplyValue}
                                        onChange={(e) => setGlobalApplyValue(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                applyGlobalValue();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={applyGlobalValue}
                                        disabled={!globalApplyValue.trim()}
                                        className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-400"
                                    >
                                        Appliquer
                                    </button>
                                    <span className="text-sm text-slate-500">
                                        {globalPricingMode === 'percent' ? '%' : 'UG'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400" />
                            <input
                                type="text"
                                value={consumablesSearch}
                                onChange={(e) => setConsumablesSearch(e.target.value)}
                                placeholder="Rechercher un produit..."
                                className="w-full pl-9 sm:pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs sm:text-sm"
                            />
                        </div>

                        {/* Products table - Desktop */}
                        <div className="hidden sm:block border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase w-16">Inclus</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Prix Marché</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Prix Client</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                    {consumablesForModal
                                        .filter(c => c.title.toLowerCase().includes(consumablesSearch.toLowerCase()))
                                        .map((consumable) => {
                                            const isSelected = selectedConsumableIds.has(consumable.id);
                                            const pricing = getProductPricing(consumable.id);
                                            const basePrice = Number(consumable.base_price ?? 0);

                                            return (
                                                <tr
                                                    key={consumable.id}
                                                    className={`transition-all ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'opacity-50 bg-slate-50 dark:bg-slate-800/30'}`}
                                                >
                                                    <td className="px-4 py-3 text-center">
                                                        <button
                                                            onClick={() => toggleConsumableSelection(consumable.id)}
                                                            className={`w-10 h-5 rounded-full transition-colors relative ${isSelected ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                                        >
                                                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isSelected ? 'left-5' : 'left-0.5'}`} />
                                                        </button>
                                                    </td>
                                                    <td className={`px-4 py-3 text-sm ${isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'}`}>
                                                        {consumable.title}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right text-slate-600">
                                                        {basePrice.toFixed(2)} €
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-right text-emerald-600 font-medium">
                                                        {isSelected ? `${(pricing.custom_price ?? 0).toFixed(2)} €` : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>

                        {/* Products list - Mobile */}
                        <div className="sm:hidden border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                            <div className="grid grid-cols-4 gap-0 px-2.5 py-2 bg-slate-50 dark:bg-slate-800 text-[10px] font-medium text-slate-500 uppercase sticky top-0">
                                <span className="text-center">Inclus</span>
                                <span>Produit</span>
                                <span className="text-right">Prix Marché</span>
                                <span className="text-right">Prix Client</span>
                            </div>
                            {consumablesForModal
                                .filter(c => c.title.toLowerCase().includes(consumablesSearch.toLowerCase()))
                                .map((consumable) => {
                                    const isSelected = selectedConsumableIds.has(consumable.id);
                                    const pricing = getProductPricing(consumable.id);
                                    const basePrice = Number(consumable.base_price ?? 0);

                                    return (
                                        <div
                                            key={consumable.id}
                                            className={`grid grid-cols-4 gap-0 items-center px-2.5 py-2 border-t border-slate-200 dark:border-slate-700 transition-all ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'opacity-50 bg-slate-50 dark:bg-slate-800/30'}`}
                                        >
                                            <div className="text-center">
                                                <button
                                                    onClick={() => toggleConsumableSelection(consumable.id)}
                                                    className={`w-9 h-[18px] rounded-full transition-colors relative ${isSelected ? 'bg-emerald-500' : 'bg-slate-300'}`}
                                                >
                                                    <span className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-transform ${isSelected ? 'left-[18px]' : 'left-0.5'}`} />
                                                </button>
                                            </div>
                                            <div className={`text-[11px] pr-1 ${isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400'} line-clamp-2`}>
                                                {consumable.title}
                                            </div>
                                            <div className="text-[11px] text-right text-slate-600">
                                                {basePrice.toFixed(2)} €
                                            </div>
                                            <div className="text-[11px] text-right text-emerald-600 font-medium">
                                                {isSelected ? `${(pricing.custom_price ?? 0).toFixed(2)} €` : '-'}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        <div className="text-center text-xs sm:text-sm text-slate-500">
                            Total produits sélectionnés: <span className="font-bold text-emerald-600">{selectedConsumableIdsForModal.size}</span>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-6 border-t border-slate-200 dark:border-slate-700">
                        <button
                            onClick={onClose}
                            className="px-3 sm:px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 sm:px-6 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            {/* Unit Type Tier Modal (gamme-level tiers) */}
            <UnitTypeTierModal
                isOpen={showUnitTypeTierModal}
                onClose={() => {
                    setShowUnitTypeTierModal(false);
                    setSelectedUnitType(null);
                }}
                unitType={selectedUnitType}
                bpuId={editing.id}
                onSuccess={handleUnitTypeTierSuccess}
            />
        </>
    );
};

export default BpuModificationModal;
